import { BFColors } from "@/constants/Colors"
import {
  BottomSheetBackdrop,
  type BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetView,
} from "@gorhom/bottom-sheet"
import * as React from "react"
import { useCallback, useEffect, useRef } from "react"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import BFText, { Body, H2, H5 } from "@/components/standard/BFText"
import { BFPressable } from "@/components/buttons/CommonButtons"
import { Button } from "@/components/reusables/ui/button"
import XIcon from "phosphor-react-native/src/icons/X"
import { showErrorToast, showSuccessToast } from "@/components/toasts/AppToast"
import { useSkipLoanProcessorKickoffCall } from "@/queries/loan-dashboard"

export const OptOutProcessorCallSpeedBump = ({
  visible,
  onClose,
  loanId,
}: {
  loanId: string
  visible: boolean
  onClose: () => void
}) => {
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)

  const optOutMutation = useSkipLoanProcessorKickoffCall()

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        onPress={() => {
          onClose()
        }}
      />
    ),
    []
  )

  function skipProcessorCall() {
    onClose()

    optOutMutation.mutate(
      { loanId },
      {
        onSuccess: () => {
          showSuccessToast({
            title: "Success",
            message:
              "You have opted out of the kickoff call. Please give it a few minutes to update the status.",
          })
        },
        onError: () => {
          showErrorToast({
            title: "Error",
            message:
              "Failed to update status. Please try again in a few minutes.",
          })
        },
      }
    )
  }

  useEffect(() => {
    if (!visible) {
      bottomSheetModalRef.current?.dismiss()
      onClose()
    } else {
      bottomSheetModalRef.current?.present()
    }
  }, [visible])

  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      backdropComponent={renderBackdrop}
      backgroundStyle={{ backgroundColor: "#FFFFFF" }}
      handleIndicatorStyle={{ backgroundColor: BFColors.offWhite }}
      enableDynamicSizing={true}
    >
      <BottomSheetView className={"flex-1 px-6"}>
        <Vertical className="items-start justify-start">
          <Horizontal className="items-start justify-end w-full py-3.5">
            <BFPressable
              testID="close-negative-roi-x"
              onPress={() => {
                onClose()
              }}
              title="Loan Dashboard Loan List Modal Close"
            >
              {(pressed) => {
                return (
                  <XIcon
                    style={{
                      opacity: pressed ? 0.5 : 1,
                    }}
                  />
                )
              }}
            </BFPressable>
          </Horizontal>
        </Vertical>
        <Vertical className={"gap-5 items-center justify-center my-10"}>
          <H2 className={"text-center"} color={"primary"}>
            No intro needed?
          </H2>
          <Body color={"primary"} className={"text-center pb-10"}>
            We’ll let your processor know you’re ready to move forward, and that
            you’ll begin uploading the required items to your portal.
          </Body>
          <Button
            variant={"reverseDestructive"}
            testID={"review-adjust"}
            className={"w-full"}
            onPress={() => {
              skipProcessorCall()
            }}
          >
            <BFText>Proceed Without Call</BFText>
          </Button>
          <H5
            className={"underline"}
            color={"primaryLight"}
            onPress={() => {
              onClose()
            }}
          >
            Cancel
          </H5>
        </Vertical>
      </BottomSheetView>
    </BottomSheetModal>
  )
}
